import React, { useState, useEffect } from 'react';
import { API_PATHS } from '../../../utils/constants';
import axios from 'axios';
import { fetchServiceMappings, fetchWorkflowServices, fetchWorkflowById } from '../../../services/workflowUtils';
import { fetchMappedServices as fetchMappedServicesUtil } from '../../../services/fetchMappedServices';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Divider,
  Alert,
  Snackbar,
  Stack
} from '@mui/material';
import AppService from '../../../Configurations/Appservice';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

// No additional component imports needed

// WorkflowAssignment component
const WorkflowAssignment = () => {
  // Theme for styling
  const theme = useTheme();
  
  // Router hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // State variables
  const [workflow, setWorkflow] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMappedServices, setLoadingMappedServices] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filteredConfigurations, setFilteredConfigurations] = useState([]);
  const [serviceConfigurations, setServiceConfigurations] = useState([]);
  const [mappedServices, setMappedServices] = useState([]);
  const [showServiceMappingDialog, setShowServiceMappingDialog] = useState(false);
  const [selectedServiceToMap, setSelectedServiceToMap] = useState(null);
  const [mappingInProgress, setMappingInProgress] = useState(false);
  
  // Function to fetch workflow details
  const fetchWorkflow = async () => {
    try {
      setLoading(true);
      
      if (!id) {
        setError('No workflow ID provided');
        return;
      }
      
      const workflowData = await fetchWorkflowById(id);
      
      if (!workflowData) {
        setError('Failed to load workflow data');
        return;
      }
      
      setWorkflow(workflowData);
      
      // Initialize service configurations from workflow steps
      if (workflowData.steps && Array.isArray(workflowData.steps)) {
        const initialConfigs = workflowData.steps.map((step, index) => ({
          workflowStepId: step.id || `step-${index}`,
          stepNumber: index + 1,
          stepName: step.name || `Step ${index + 1}`,
          serviceId: step.serviceId || step.id || `service-${index}`,
          serviceName: step.serviceName || step.name || `Service ${index + 1}`,
          required: step.required !== undefined ? step.required : true,
          enabled: true,
          pricingOverride: {
            governmentCharge: step.governmentCharge || '',
            serviceCharge: step.serviceCharge || '',
            taxRate: step.taxRate || ''
          }
        }));
        
        setServiceConfigurations(initialConfigs);
      } else if (workflowData.definition) {
        // Try to parse the definition if it's a string
        let definition = workflowData.definition;
        if (typeof definition === 'string') {
          try {
            definition = JSON.parse(definition);
          } catch (error) {
            console.error('Failed to parse workflow definition:', error);
          }
        }
        
        if (definition && definition.steps && Array.isArray(definition.steps)) {
          const initialConfigs = definition.steps.map((step, index) => ({
            workflowStepId: step.id || `step-${index}`,
            stepNumber: index + 1,
            stepName: step.name || `Step ${index + 1}`,
            serviceId: step.serviceId || step.id || `service-${index}`,
            serviceName: step.serviceName || step.name || `Service ${index + 1}`,
            required: step.required !== undefined ? step.required : true,
            enabled: true,
            pricingOverride: {
              governmentCharge: step.governmentCharge || '',
              serviceCharge: step.serviceCharge || '',
              taxRate: step.taxRate || ''
            }
          }));
          
          setServiceConfigurations(initialConfigs);
        }
      }
    } catch (error) {
      console.error('Error fetching workflow:', error);
      setError('Failed to load workflow. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to fetch customers
  const fetchCustomers = async () => {
    try {
      // Try to ensure we have a valid token first
      try {
        const authService = await import('../../../services/auth');
        let token = authService.getToken();
        
        // If no token, try to login
        if (!token) {
          console.log('[WorkflowAssignment] No token found, attempting to login...');
          const loginSuccess = await authService.login('admin', 'admin');
          if (loginSuccess) {
            token = authService.getToken();
            console.log('[WorkflowAssignment] Login successful, token obtained');
          } else {
            console.error('[WorkflowAssignment] Login failed');
          }
        }
        
        if (token) {
          console.log('[WorkflowAssignment] Fetching customers with token auth...');
          
          // Try multiple endpoints with token auth - based on the actual API structure in OrganisationController.java
          const endpoints = [
            `${API_PATHS.API}/organisations?type=CUSTOMER`,
            `${API_PATHS.V1}/organisation?type=CUSTOMER`,
            `${API_PATHS.API}/organisations/keyValues?type=CUSTOMER`,
            `${API_PATHS.API}/organisations`,
            `${API_PATHS.API}/organizations`,
            `${API_PATHS.API}/customers`
          ];
          
          for (const endpoint of endpoints) {
            try {
              console.log(`[WorkflowAssignment] Trying endpoint with token: ${endpoint}`);
              const response = await axios.get(endpoint, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response && response.data) {
                console.log(`[WorkflowAssignment] Successfully fetched customers from ${endpoint}`);
                setCustomers(response.data);
                return;
              }
            } catch (endpointError) {
              console.warn(`[WorkflowAssignment] Failed to fetch from ${endpoint} with token:`, endpointError.message);
            }
          }
        }
      } catch (tokenError) {
        console.warn('[WorkflowAssignment] Token auth failed:', tokenError);
      }
      
      // Fallback to AppService
      console.log('[WorkflowAssignment] Trying AppService for customers...');
      try {
        // Try multiple possible endpoints based on the actual API structure in OrganisationController.java
        const endpoints = [
          'api/organisations?type=CUSTOMER',
          'v1/organisation?type=CUSTOMER',
          'api/organisations/keyValues?type=CUSTOMER',
          'api/organisations',
          'api/organizations'
        ];
        
        for (const endpoint of endpoints) {
          try {
            console.log(`[WorkflowAssignment] Trying AppService endpoint: ${endpoint}`);
            const response = await AppService.get(endpoint);
            if (response && response.data) {
              console.log(`[WorkflowAssignment] Successfully fetched customers from ${endpoint}`);
              setCustomers(response.data);
              return;
            }
          } catch (endpointError) {
            console.warn(`[WorkflowAssignment] Failed to fetch from ${endpoint}:`, endpointError.message);
          }
        }
        
        // If we get here, all endpoints failed
        throw new Error('All customer endpoints failed');
      } catch (appServiceError) {
        console.error('[WorkflowAssignment] AppService fallback failed:', appServiceError);
        throw appServiceError;
      }
    } catch (error) {
      console.error('[WorkflowAssignment] Error fetching customers:', error);
      setError('Failed to load customers. Please try again.');
    }
  };
  
  // Function to fetch mapped services for a customer organization.
  // The workflow's services are already loaded (see fetchWorkflow); here we only need to know which
  // of them are already mapped to the selected customer. That comes from the single working endpoint
  // GET /v1/serviceMapping/mappedServices/{organisationId}, so we call it directly instead of going
  // through fetchWorkflowServices (which retries several non-existent /steps and /services URLs).
  const fetchMappedServices = async (customerId) => {
    if (!customerId) {
      console.log('No customer ID provided');
      return;
    }

    setLoadingMappedServices(true);

    try {
      console.log(`[WorkflowAssignment] Fetching mapped services for customer ID: ${customerId}`);

      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_PATHS.V1}/serviceMapping/mappedServices/${customerId}`,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          }
        }
      );

      const mappedList = Array.isArray(response?.data) ? response.data : [];
      setMappedServices(mappedList);

      // Index the customer's mapped services by service id (ServiceResponse.id is the service id).
      const mappedById = new Map();
      mappedList.forEach(m => {
        const sid = String(m.id || m.serviceId || '').trim().toLowerCase();
        if (sid) mappedById.set(sid, m);
      });

      // Flag each workflow service as mapped/unmapped based on the customer's mappings.
      setServiceConfigurations(prevConfigs => prevConfigs.map(config => {
        const sid = String(config.serviceId || '').trim().toLowerCase();
        const mapping = sid ? mappedById.get(sid) : null;
        if (!mapping) {
          return { ...config, isMapped: false, governmentCharge: '', serviceCharge: '', taxRate: '' };
        }
        return {
          ...config,
          isMapped: true,
          governmentCharge: mapping.govtCharge != null ? String(mapping.govtCharge) : '',
          serviceCharge: mapping.serviceCharge != null ? String(mapping.serviceCharge) : '',
          taxRate: mapping.taxOnServiceCharge != null ? String(mapping.taxOnServiceCharge) : ''
        };
      }));
    } catch (error) {
      console.error('Error fetching mapped services:', error);
      setError(`Error fetching mapped services: ${error.message}`);
    } finally {
      setLoadingMappedServices(false);
    }
  };
  
  // Function to update service configurations with mapping information
  const updateServiceConfigurationsWithMappingInfo = (mappedServicesData) => {
    if (!serviceConfigurations.length) {
      console.log('No service configurations to update');
      return;
    }
    
    console.log('[WorkflowAssignment] Updating service configurations with mapping info');
    
    // First, separate truly mapped services from unmapped ones
    // This is critical because the mappedServicesData may contain both mapped and unmapped services
    // We need to check both the isMapped flag and the actual mapping data
    const trulyMappedServices = mappedServicesData.filter(service => {
      // A service is truly mapped if it has the isMapped flag AND has valid mapping data
      return service.isMapped === true && 
             (service.governmentCharge || service.govtCharge || 
              service.serviceCharge || 
              service.taxRate || service.taxOnServiceCharge);
    });
    
    console.log('[WorkflowAssignment] Truly mapped services:', trulyMappedServices);
    console.log('[WorkflowAssignment] Total services in workflow:', serviceConfigurations.length);
    
    // If no truly mapped services, mark all as unmapped
    if (!trulyMappedServices || trulyMappedServices.length === 0) {
      console.log('[WorkflowAssignment] No truly mapped services found - marking all as unmapped');
      const unmappedConfigurations = serviceConfigurations.map(config => ({
        ...config,
        isMapped: false,
        governmentCharge: '',
        serviceCharge: '',
        taxRate: ''
      }));
      setServiceConfigurations(unmappedConfigurations);
      console.log('[WorkflowAssignment] Updated configurations:', unmappedConfigurations);
      return;
    }
    
    const updatedConfigurations = serviceConfigurations.map(config => {
      // Normalize IDs for comparison
      const configServiceId = String(config.serviceId || '').trim().toLowerCase();
      const configServiceName = String(config.serviceName || '').trim().toLowerCase();
      
      console.log(`[WorkflowAssignment] Checking mapping for service: ${configServiceName} (ID: ${configServiceId})`);
      
      // IMPORTANT: Only look for this service in the truly mapped services list
      // Find matching mapped service using ONLY exact ID match (most reliable)
      let mappedService = trulyMappedServices.find(service => {
        // Normalize service IDs for comparison - handle all possible field names
        const serviceId = String(service.serviceId || service.id || service.service_id || '').trim().toLowerCase();
        
        // Check for exact ID match (most reliable)
        const idMatch = serviceId && configServiceId && serviceId === configServiceId;
        
        if (idMatch) {
          console.log(`[WorkflowAssignment] Found mapping match by ID:`);
          console.log(`  - ID match: true (${serviceId} vs ${configServiceId})`);
          console.log(`  - Service has mapping data:`, {
            governmentCharge: service.governmentCharge || service.govtCharge,
            serviceCharge: service.serviceCharge,
            taxRate: service.taxRate || service.taxOnServiceCharge
          });
          return true;
        }
        
        return false;
      });
      
      // If no ID match found, try name match as fallback
      if (!mappedService) {
        mappedService = trulyMappedServices.find(service => {
          // Normalize service names for comparison
          const serviceName = String(service.serviceName || service.name || service.service_name || '').trim().toLowerCase();
          
          // Check for exact name match
          const exactNameMatch = serviceName && configServiceName && serviceName === configServiceName;
          
          if (exactNameMatch) {
            console.log(`[WorkflowAssignment] Found mapping match by name:`);
            console.log(`  - Exact name match: true (${serviceName} vs ${configServiceName})`);
            return true;
          }
          
          return false;
        });
      }
      
      // No special cases - rely solely on API data
      const isSpecialCase = false;
      
      if (mappedService) {
        console.log(`[WorkflowAssignment] Service is mapped:`, mappedService);
        
        // Handle all possible field names for pricing data
        // Government Charge - try all possible field names
        const governmentCharge = 
          mappedService?.govtCharge || 
          mappedService?.govt_charge ||
          mappedService?.governmentCharge || 
          mappedService?.government_charge ||
          mappedService?.GovernmentCharge ||
          config.pricingOverride?.governmentCharge || 
          config.pricingOverride?.govtCharge || 
          '';
        
        // Service Charge - try all possible field names
        const serviceCharge = 
          mappedService?.serviceCharge || 
          mappedService?.service_charge ||
          mappedService?.ServiceCharge ||
          config.pricingOverride?.serviceCharge || 
          '';
        
        // Tax Rate - try all possible field names
        const taxRate = 
          mappedService?.taxRate || 
          mappedService?.tax_rate ||
          mappedService?.taxOnServiceCharge || 
          mappedService?.tax_on_service_charge ||
          mappedService?.TaxRate ||
          config.pricingOverride?.taxRate || 
          config.pricingOverride?.taxOnServiceCharge || 
          '';
        
        console.log(`[WorkflowAssignment] Extracted pricing data:`, {
          governmentCharge,
          serviceCharge,
          taxRate,
          originalMapping: mappedService
        });
        
        return {
          ...config,
          mappedServiceId: mappedService.id || mappedService.serviceId, // Store the ID of the mapped service
          isMapped: true,
          governmentCharge,
          serviceCharge,
          taxRate,
          // Include original fields for reference
          govtCharge: governmentCharge,
          taxOnServiceCharge: taxRate,
          isDefaultPricing: false // Flag to indicate this is mapped pricing
        };
      } else {
        console.log(`[WorkflowAssignment] Service is NOT mapped:`, config);
        
        // Get default pricing from service configuration without hardcoded values
        let defaultGovernmentCharge = '';
        let defaultServiceCharge = '';
        let defaultTaxRate = '';
        
        // Use values from config object, which should come from the backend
        // No hardcoded values - use what's available in the config or empty string
        defaultGovernmentCharge = config.governmentCharge || 
                               config.govtCharge || 
                               config.pricingOverride?.governmentCharge || 
                               config.defaultGovernmentCharge || 
                               '';
        
        defaultServiceCharge = config.serviceCharge || 
                           config.pricingOverride?.serviceCharge || 
                           config.defaultServiceCharge || 
                           '';
        
        defaultTaxRate = config.taxRate || 
                     config.taxOnServiceCharge || 
                     config.pricingOverride?.taxRate || 
                     config.defaultTaxRate || 
                     '';
        
        console.log(`[WorkflowAssignment] Using default pricing for unmapped service:`, {
          defaultGovernmentCharge,
          defaultServiceCharge,
          defaultTaxRate
        });
        
        return {
          ...config,
          isMapped: false,
          governmentCharge: defaultGovernmentCharge,
          serviceCharge: defaultServiceCharge,
          taxRate: defaultTaxRate,
          isDefaultPricing: true // Flag to indicate this is default pricing
        };
      }
    });
    
    // Log the mapping status for each service for debugging
    const mappingStatusSummary = updatedConfigurations.map(config => ({
      serviceName: config.serviceName || config.name,
      serviceId: config.serviceId,
      isMapped: config.isMapped,
      hasDefaultPricing: config.isDefaultPricing || false
    }));
    
    console.log('[WorkflowAssignment] Final mapping status summary:', mappingStatusSummary);
    console.log('[WorkflowAssignment] Updated configurations:', updatedConfigurations);
    
    setServiceConfigurations(updatedConfigurations);
    setFilteredConfigurations(updatedConfigurations);
  };
  
  // Function to handle customer selection change
  const handleCustomerChange = (event) => {
    const customerId = event.target.value;
    setSelectedCustomer(customerId);
    
    if (customerId) {
      fetchMappedServices(customerId);
    } else {
      setMappedServices([]);
      
      // Reset mapping status in service configurations
      const resetConfigurations = serviceConfigurations.map(config => ({
        ...config,
        isMapped: false
      }));
      
      setServiceConfigurations(resetConfigurations);
      setFilteredConfigurations(resetConfigurations);
    }
  };
  
  // Function to toggle service enablement
  const handleServiceToggle = (serviceId, enabled) => {
    const updatedConfigurations = serviceConfigurations.map(config => {
      if (config.serviceId === serviceId) {
        return {
          ...config,
          enabled
        };
      }
      return config;
    });
    
    setServiceConfigurations(updatedConfigurations);
  };
  
  // Function to update pricing for a service - now read-only
  // This function is kept for compatibility but no longer allows editing pricing
  const handlePricingUpdate = (serviceId, field, value) => {
    console.log(`[WorkflowAssignment] Pricing update attempted but pricing is read-only. Service: ${serviceId}, Field: ${field}`);
    // No longer updating pricing as service mapping is the source of truth
    return;
  };
  
  // Function to save workflow assignment
  const handleSaveAssignment = async () => {
    try {
      // Validate that a customer is selected
      if (!selectedCustomer) {
        setError('Please select a customer organization');
        return;
      }
      
      console.log('[WorkflowAssignment] Preparing to save assignment with customer ID:', selectedCustomer);
      
      // Check if any required services are not mapped
      const unmappedRequiredServices = serviceConfigurations.filter(
        config => config.required && config.enabled && !config.isMapped
      );
      
      if (unmappedRequiredServices.length > 0) {
        setShowServiceMappingDialog(true);
        return;
      }
      
      // Prepare data for API call
      const assignmentData = {
        workflowId: id,
        customerId: selectedCustomer, // Changed from organisationId to customerId as expected by the backend
        variant: 'DEFAULT', // Add variant field which might be required by the backend
        enabled: true, // Add enabled field to indicate this assignment is active
        services: serviceConfigurations
          .filter(config => config.enabled)
          .map(config => ({
            serviceId: config.serviceId,
            required: config.required
            // Removed pricingOverride as service mapping is the source of truth
          }))
      };
      
      // Ensure all required fields are present and not null
      if (!assignmentData.workflowId) {
        setError('Workflow ID is missing');
        return;
      }
      
      if (!assignmentData.customerId) {
        setError('Customer ID is missing');
        return;
      }
      
      // Log the selected customer ID for debugging
      console.log('[WorkflowAssignment] Selected customer ID:', selectedCustomer);
      
      // Make API call to assign workflow using the correct endpoint
      console.log('[WorkflowAssignment] Sending assignment data:', assignmentData);
      
      // Use the correct API endpoint for workflow assignments with proper error handling
      try {
        console.log('[WorkflowAssignment] Sending API request to:', 'api/workflow-assignments');
        const response = await AppService.post('api/workflow-assignments', assignmentData);
        
        console.log('[WorkflowAssignment] Assignment successful:', response.data);
        setSuccess('Workflow assigned successfully');
        // Optionally navigate to another page
        // navigate('/workflows/assignments');
        
        return response;
      } catch (apiError) {
        console.error('[WorkflowAssignment] API error:', apiError.response?.data || apiError.message);
        setError(apiError.response?.data?.message || 'Failed to assign workflow. Please try again.');
        throw apiError;
      }
    } catch (error) {
      console.error('Error saving workflow assignment:', error);
      setError('Failed to save workflow assignment. Please try again.');
    }
  };
  
  // Function to close service mapping dialog
  const handleCloseServiceMappingDialog = () => {
    setShowServiceMappingDialog(false);
    setSelectedServiceToMap(null);
  };
  
  // Function to navigate to service mapping page
  const handleGoToServiceMapping = () => {
    handleCloseServiceMappingDialog();
    navigate('/admin/service-mapping');
  };
  
  // Function to handle mapping a service to the selected customer organization
  const handleMapService = async () => {
    if (!selectedCustomer || !selectedServiceToMap) {
      setError('Please select a customer organization and service first');
      return;
    }
    
    setMappingInProgress(true);
    
    try {
      // Prepare the service mapping data
      const mappingData = {
        serviceId: selectedServiceToMap.id,
        organizationId: selectedCustomer,
        serviceCharge: '0',
        taxOnServiceCharge: '0',
        govtCharge: '0'
      };
      
      // Get the token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Make the API call to create the service mapping
      const response = await axios.post(
        `${API_PATHS.V1}/serviceMapping`,
        mappingData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200 || response.status === 201) {
        setSuccess(`Successfully mapped service "${selectedServiceToMap.name}" to the selected customer organization`);
        
        // Refresh the mapped services data to update the UI
        await fetchMappedServices(
          selectedCustomer,
          id,
          workflow,
          serviceConfigurations,
          setLoadingMappedServices,
          setError,
          setMappedServices,
          updateServiceConfigurationsWithMappingInfo,
          fetchWorkflowServices,
          fetchServiceMappings
        );
      } else {
        setError(`Failed to map service: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error mapping service:', error);
      setError(`Error mapping service: ${error.message}`);
    } finally {
      setMappingInProgress(false);
      setSelectedServiceToMap(null);
      setShowServiceMappingDialog(false);
    }
  };
  
  // Function to handle mapping a service with default price
  const handleMapServiceWithDefaultPrice = async (serviceId, serviceName) => {
    if (!selectedCustomer) {
      enqueueSnackbar('Please select a customer organization first', { variant: 'error' });
      return;
    }
    
    if (!serviceId) {
      enqueueSnackbar('Invalid service selected', { variant: 'error' });
      return;
    }
    
    setMappingInProgress(true);
    
    try {
      console.log(`[WorkflowAssignment] Mapping service ${serviceName} (${serviceId}) with default price`);
      
      // Get the token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // First, fetch the actual service details from backend to get real default pricing values
      console.log(`[WorkflowAssignment] Fetching service details for ID: ${serviceId}`);
      let serviceDetails;
      try {
        const serviceDetailsResponse = await axios.get(
          `${API_PATHS.V1}/service/details?serviceId=${serviceId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        serviceDetails = serviceDetailsResponse.data;
        console.log('[WorkflowAssignment] Service details fetched successfully:', serviceDetails);
      } catch (detailsError) {
        console.error('[WorkflowAssignment] Error fetching service details:', detailsError);
        // Continue with local config as fallback
      }
      
      // Find the service configuration as fallback
      const serviceConfig = serviceConfigurations.find(config => config.serviceId === serviceId);
      
      if (!serviceConfig && !serviceDetails) {
        throw new Error(`Service configuration not found for service ID: ${serviceId}`);
      }
      
      // Get default pricing from service details (backend) first, then fall back to local config
      // This ensures we use the real default values from the backend service entity
      const defaultGovernmentCharge = 
                                   (serviceDetails?.governmentCharge || 
                                   serviceDetails?.govtCharge || 
                                   serviceDetails?.defaultGovernmentCharge) ||
                                   serviceConfig?.governmentCharge || 
                                   serviceConfig?.govtCharge || 
                                   serviceConfig?.pricingOverride?.governmentCharge || 
                                   String(serviceConfig?.defaultGovernmentCharge || '') || 
                                   '0';
      
      const defaultServiceCharge = 
                               (serviceDetails?.serviceCharge || 
                               serviceDetails?.defaultServiceCharge) ||
                               serviceConfig?.serviceCharge || 
                               serviceConfig?.pricingOverride?.serviceCharge || 
                               String(serviceConfig?.defaultServiceCharge || '') || 
                               '0';
      
      const defaultTaxRate = 
                         (serviceDetails?.taxRate || 
                         serviceDetails?.taxOnServiceCharge || 
                         serviceDetails?.defaultTaxRate) ||
                         serviceConfig?.taxRate || 
                         serviceConfig?.taxOnServiceCharge || 
                         serviceConfig?.pricingOverride?.taxRate || 
                         String(serviceConfig?.defaultTaxRate || '') || 
                         '0';
      
      // Prepare the service mapping data with default pricing
      const mappingData = {
        serviceId: serviceId,
        organisationId: selectedCustomer, // Using British spelling as expected by backend
        serviceCharge: defaultServiceCharge,
        taxOnServiceCharge: defaultTaxRate,
        govtCharge: defaultGovernmentCharge
      };
      
      console.log('[WorkflowAssignment] Mapping data with default pricing:', mappingData);
      
      // Token already retrieved above, no need to get it again
      
      // Make the API call to create the service mapping
      const response = await axios.post(
        `${API_PATHS.V1}/serviceMapping`,
        mappingData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200 || response.status === 201) {
        enqueueSnackbar(`Successfully mapped service "${serviceName}" with default pricing`, { variant: 'success' });
        
        // Refresh the mapped services data to update the UI
        await fetchMappedServices(selectedCustomer);
      } else {
        enqueueSnackbar(`Failed to map service: ${response.statusText}`, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error mapping service with default price:', error);
      enqueueSnackbar(`Error mapping service: ${error.message}`, { variant: 'error' });
    } finally {
      setMappingInProgress(false);
    }
  };
  
  // Function to close error alert
  const handleCloseError = () => {
    setError(null);
  };
  
  // Function to close success alert
  const handleCloseSuccess = () => {
    setSuccess(null);
  };
  
  // Check if we're returning from service mapping page
  useEffect(() => {
    const returnedFromMapping = sessionStorage.getItem('returnedFromServiceMapping');
    const mappedCustomerId = sessionStorage.getItem('mappedCustomerId');
    
    if (returnedFromMapping === 'true' && mappedCustomerId) {
      console.log('[WorkflowAssignment] Detected return from service mapping page');
      // Set the customer ID and refresh the mappings
      setSelectedCustomer(mappedCustomerId);
      fetchMappedServices(mappedCustomerId);
      
      // Clear the session storage
      sessionStorage.removeItem('returnedFromServiceMapping');
      sessionStorage.removeItem('mappedCustomerId');
    }
  }, []);
  
  // Main useEffect to fetch workflow and initialize component
  useEffect(() => {
    fetchWorkflow();
    fetchCustomers();
  }, [id]);
  
  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Render error state
  if (error && !workflow) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  // Main render
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Assign Workflow: {workflow?.name}
      </Typography>
      
      <Typography variant="body1" paragraph>
        {workflow?.description}
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Customer
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="customer-select-label">Customer Organization</InputLabel>
          <Select
            labelId="customer-select-label"
            id="customer-select"
            value={selectedCustomer}
            label="Customer Organization"
            onChange={handleCustomerChange}
          >
            <MenuItem value="">
              <em>Select a customer</em>
            </MenuItem>
            {customers.map((customer) => (
              <MenuItem key={customer.id} value={customer.id}>
                {customer.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>
      
      {loadingMappedServices && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}
      
      {selectedCustomer && !loadingMappedServices && (
        <>
          {/* Mapping Summary Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Service Mapping Summary
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 2, bgcolor: theme.palette.background.default, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">Total Services</Typography>
                  <Typography variant="h4">{serviceConfigurations.length}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 2, bgcolor: theme.palette.success.light, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">Mapped Services</Typography>
                  <Typography variant="h4">{serviceConfigurations.filter(config => config.isMapped).length}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 2, bgcolor: theme.palette.error.light, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">Unmapped Services</Typography>
                  <Typography variant="h4">{serviceConfigurations.filter(config => !config.isMapped).length}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 2, bgcolor: theme.palette.info.light, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">Mapping Percentage</Typography>
                  <Typography variant="h4">
                    {serviceConfigurations.length > 0 
                      ? `${(serviceConfigurations.filter(config => config.isMapped).length / serviceConfigurations.length * 100).toFixed(0)}%` 
                      : '0%'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Service Configuration Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configure Services
            </Typography>
            
            <Typography variant="body2" paragraph>
              Enable or disable services for this workflow assignment. Services marked as required cannot be disabled.
            </Typography>
            
            {/* Filter Controls */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={() => setFilteredConfigurations(serviceConfigurations)}
                sx={{
                  color: '#000000',
                  fontWeight: 'bold',
                  borderColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                    color: '#000000'
                  }
                }}
              >
                All Services
              </Button>
              <Button 
                variant="outlined" 
                color="success"
                onClick={() => setFilteredConfigurations(serviceConfigurations.filter(config => config.isMapped))}
                sx={{
                  color: '#000000',
                  fontWeight: 'bold',
                  borderColor: theme.palette.success.main,
                  '&:hover': {
                    backgroundColor: theme.palette.success.light,
                    color: '#000000'
                  }
                }}
              >
                Mapped Only
              </Button>
              <Button 
                variant="outlined" 
                color="error"
                onClick={() => setFilteredConfigurations(serviceConfigurations.filter(config => !config.isMapped))}
                sx={{
                  color: '#000000',
                  fontWeight: 'bold',
                  borderColor: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: theme.palette.error.light,
                    color: '#000000'
                  }
                }}
              >
                Unmapped Only
              </Button>
            </Box>
            
            <Grid container spacing={3}>
              {filteredConfigurations.map((config) => (
                <Grid item xs={12} md={6} lg={4} key={config.serviceId}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      borderLeft: config.required ? `4px solid ${theme.palette.primary.main}` : 'none',
                      borderColor: config.isMapped ? theme.palette.success.main : theme.palette.error.main,
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Mapping status indicator */}
                    <Box 
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '100%',
                        height: '5px',
                        backgroundColor: config.isMapped ? theme.palette.success.main : theme.palette.error.main,
                      }}
                    />
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 1,
                      p: 1,
                      backgroundColor: config.isMapped ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                      borderRadius: 1
                    }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {config.serviceName}
                      </Typography>
                      
                      <Chip
                        label={config.isMapped ? 'Mapped' : 'Not Mapped'}
                        color={config.isMapped ? 'success' : 'error'}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          border: '1px solid',
                          borderColor: config.isMapped ? theme.palette.success.dark : theme.palette.error.dark,
                          backgroundColor: config.isMapped ? theme.palette.success.main : theme.palette.error.main,
                          '& .MuiChip-label': {
                            color: '#ffffff',
                            fontWeight: 'bold',
                            textShadow: '0px 1px 2px rgba(0,0,0,0.2)'
                          }
                        }}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Service ID: {config.serviceId}
                    </Typography>
                    
                    {/* Pricing Information */}
                    <Box sx={{ 
                      mb: 2, 
                      p: 1, 
                      borderRadius: 1, 
                      backgroundColor: 'rgba(33, 150, 243, 0.1)',
                      border: '1px dashed',
                      borderColor: 'primary.main'
                    }}>
                      <Typography variant="caption" color="primary" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
                        Pricing
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Govt Charge:
                          </Typography>
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            value={config.governmentCharge || ''}
                            disabled={true}
                            placeholder="Govt Charge"
                            InputProps={{
                              readOnly: true,
                              sx: { 
                                height: '32px', 
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                color: '#000000',
                                backgroundColor: '#f5f5f5'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Service Charge:
                          </Typography>
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            value={config.serviceCharge || ''}
                            disabled={true}
                            placeholder="Service Charge"
                            InputProps={{
                              readOnly: true,
                              sx: { 
                                height: '32px', 
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                color: '#000000',
                                backgroundColor: '#f5f5f5'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Tax Rate:
                          </Typography>
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            value={config.taxRate || ''}
                            disabled={true}
                            placeholder="Tax Rate"
                            InputProps={{
                              readOnly: true,
                              sx: { 
                                height: '32px', 
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                color: '#000000',
                                backgroundColor: '#f5f5f5'
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.enabled}
                          onChange={(e) => handleServiceToggle(config.serviceId, e.target.checked)}
                          disabled={config.required || !config.isMapped}
                        />
                      }
                      label={config.enabled ? 'Enabled' : 'Disabled'}
                    />
                  
                    {!config.isMapped && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                          This service is not mapped for the selected customer.
                        </Typography>
                        <Stack spacing={1}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            fullWidth
                            onClick={() => {
                              // Navigate to the service mapping page with customer and service pre-selected
                              const customerName = customers.find(c => c.id === selectedCustomer)?.name || '';
                              const serviceId = config.serviceId;
                              const serviceName = config.serviceName;
                              
                              console.log('[WorkflowAssignment] Navigating to service mapping with:', {
                                customerId: selectedCustomer,
                                customerName,
                                serviceId,
                                serviceName
                              });
                              
                              // First, clear any existing session storage to prevent conflicts
                              sessionStorage.removeItem('selectedCustomerId');
                              sessionStorage.removeItem('selectedCustomerName');
                              sessionStorage.removeItem('selectedServiceId');
                              sessionStorage.removeItem('selectedServiceName');
                              sessionStorage.removeItem('fromWorkflowAssignment');
                              
                              // Then set the new values in session storage
                              sessionStorage.setItem('selectedCustomerId', selectedCustomer);
                              sessionStorage.setItem('selectedCustomerName', customerName);
                              sessionStorage.setItem('selectedServiceId', serviceId);
                              sessionStorage.setItem('selectedServiceName', serviceName);
                              sessionStorage.setItem('fromWorkflowAssignment', 'true');
                              
                              // Set flag to indicate we need to refresh on return
                              sessionStorage.setItem('returnedFromServiceMapping', 'true');
                              sessionStorage.setItem('mappedCustomerId', selectedCustomer);
                              
                              // Log session storage before navigation
                              console.log('[WorkflowAssignment] Session storage before navigation:', {
                                selectedCustomerId: sessionStorage.getItem('selectedCustomerId'),
                                selectedCustomerName: sessionStorage.getItem('selectedCustomerName'),
                                selectedServiceId: sessionStorage.getItem('selectedServiceId'),
                                selectedServiceName: sessionStorage.getItem('selectedServiceName'),
                                fromWorkflowAssignment: sessionStorage.getItem('fromWorkflowAssignment')
                              });
                              
                              // Use a longer delay to ensure session storage is set before navigation
                              setTimeout(() => {
                                // Create state object for navigation
                                const navigationState = {
                                  selectedCustomerId: selectedCustomer,
                                  selectedCustomerName: customerName,
                                  selectedServiceId: serviceId,
                                  selectedServiceName: serviceName,
                                  fromWorkflowAssignment: true
                                };
                                
                                console.log('[WorkflowAssignment] Navigating with state:', navigationState);
                                
                                // Navigate to the service mapping page
                                navigate('/ADM-Customer/adm-customer-service-mapping', {
                                  state: navigationState,
                                  replace: true // Use replace to ensure proper history handling
                                });
                                
                                // Log again after navigation
                                setTimeout(() => {
                                  console.log('[WorkflowAssignment] After navigation, session storage:', {
                                    selectedCustomerId: sessionStorage.getItem('selectedCustomerId'),
                                    selectedCustomerName: sessionStorage.getItem('selectedCustomerName'),
                                    selectedServiceId: sessionStorage.getItem('selectedServiceId'),
                                    selectedServiceName: sessionStorage.getItem('selectedServiceName'),
                                    fromWorkflowAssignment: sessionStorage.getItem('fromWorkflowAssignment')
                                  });
                                }, 100);
                              }, 300);
                            }}
                            disabled={mappingInProgress}
                            sx={{
                              color: '#000000',
                              fontWeight: 'bold',
                              backgroundColor: theme.palette.primary.light,
                              '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                                color: '#000000'
                              }
                            }}
                          >
                            {mappingInProgress ? <CircularProgress size={24} /> : 'Map Service'}
                          </Button>
                          
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            fullWidth
                            onClick={() => handleMapServiceWithDefaultPrice(config.serviceId, config.serviceName)}
                            disabled={mappingInProgress}
                            sx={{
                              color: '#000000',
                              fontWeight: 'bold',
                              backgroundColor: theme.palette.secondary.light,
                              '&:hover': {
                                backgroundColor: theme.palette.secondary.main,
                                color: '#000000'
                              }
                            }}
                          >
                            {mappingInProgress ? <CircularProgress size={24} /> : 'Map With Default Price'}
                          </Button>
                        </Stack>
                      </Box>
                    )}
                  
                    <Divider sx={{ my: 2 }} />
                    
                    {/* <Typography variant="subtitle2" gutterBottom>
                      Pricing Override
                    </Typography>
                  
                    <TextField
                      label="Government Charge"
                      variant="outlined"
                      size="small"
                      fullWidth
                      margin="dense"
                      value={config.pricingOverride?.governmentCharge || ''}
                      onChange={(e) => handlePricingUpdate(config.serviceId, 'governmentCharge', e.target.value)}
                      disabled={!config.enabled || !config.isMapped}
                    />
                  
                    <TextField
                      label="Service Charge"
                      variant="outlined"
                      size="small"
                      fullWidth
                      margin="dense"
                      value={config.pricingOverride?.serviceCharge || ''}
                      onChange={(e) => handlePricingUpdate(config.serviceId, 'serviceCharge', e.target.value)}
                      disabled={!config.enabled || !config.isMapped}
                    />
                  
                    <TextField
                      label="Tax Rate"
                      variant="outlined"
                      size="small"
                      fullWidth
                      margin="dense"
                      value={config.pricingOverride?.taxRate || ''}
                      onChange={(e) => handlePricingUpdate(config.serviceId, 'taxRate', e.target.value)}
                      disabled={!config.enabled || !config.isMapped}
                    /> */}
                  </Paper>
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveAssignment}
                disabled={loadingMappedServices}
              >
                Save Assignment
              </Button>
            </Box>
          </Paper>
        </>
      )}
      
      {/* {workflow && (
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Grid container spacing={3}>
          
          <Grid item xs={12}>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveAssignment}
                disabled={loadingMappedServices}
              >
                Save Assignment
              </Button>
            </Box>
          </Grid>
          </Grid>
        </Paper>
      )} */}
      
      {/* Service Mapping Dialog */}
      <Dialog
        open={showServiceMappingDialog}
        onClose={handleCloseServiceMappingDialog}
      >
        <DialogTitle>
          {selectedServiceToMap ? `Map Service: ${selectedServiceToMap.name}` : 'Service Mapping Required'}
        </DialogTitle>
        <DialogContent>
          {selectedServiceToMap ? (
            <DialogContentText>
              Do you want to map the service "{selectedServiceToMap.name}" to the selected customer organization?
              This will create a default mapping with zero charges that you can adjust later.
            </DialogContentText>
          ) : (
            <DialogContentText>
              Some required services are not mapped for the selected customer organization.
              You need to map these services before you can assign this workflow.
              Would you like to go to the Service Mapping section now?
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseServiceMappingDialog}>Cancel</Button>
          {selectedServiceToMap ? (
            <Button 
              onClick={handleMapService} 
              color="primary"
              disabled={mappingInProgress}
            >
              {mappingInProgress ? <CircularProgress size={24} /> : 'Map Service'}
            </Button>
          ) : (
            <Button onClick={handleGoToServiceMapping} color="primary">
              Go to Service Mapping
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkflowAssignment;
